import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Eye, ExternalLink, Loader2, Monitor, RefreshCw, Smartphone, Sparkles, Tablet } from 'lucide-react';
import { toast } from 'sonner';
import {
  buildReactPreviewFromFiles,
  isAppShellHtml,
  isCatalogHtml,
  isStaleReactPreviewHtml,
  isViteReactScaffold,
  resolvePreviewDocument,
  resolvePreviewEngine,
  shouldUseSandpack,
  isSandpackRenderable,
  canBuildInstantReactPreview,
  filesToSandpack,
} from '../../utils/previewHtml';
import SandpackPreview from './SandpackPreview';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const DEVICE_WIDTHS = {
  desktop: '100%',
  tablet: '768px',
  mobile: '390px',
};

const PREVIEW_SANDBOX = 'allow-scripts allow-forms allow-popups allow-same-origin';

const BUILD_STEPS = ['plan', 'codegen', 'build', 'preview'];

function getPreviewLoadingCopy({
  isLoadingProject,
  projectId,
  isGenerating,
  isBuilding,
  buildStepId,
  appType,
  e2bApplicable,
  e2bStatus,
  vitePreviewLoading = false,
  instantPreviewReady = false,
  projectState = 'idle',
  hasFiles = false,
}) {
  if (isLoadingProject) {
    return {
      title: 'Loading your project…',
      subtitle: 'Fetching your saved work from the server.',
      active: true,
    };
  }
  if (projectState === 'awaiting_clarification') {
    return {
      title: 'Answer the questions in chat',
      subtitle: 'Your preview will appear once you reply and the build continues.',
      active: false,
    };
  }
  if (projectState === 'awaiting_requirements') {
    return {
      title: 'Pick options in chat',
      subtitle: 'Select your preferences above, or type a custom answer.',
      active: false,
    };
  }
  if (!projectId && !isGenerating) {
    return {
      title: 'Your preview will appear here',
      subtitle: 'Describe your app in the chat to get started.',
      active: false,
    };
  }
  if (e2bApplicable && ['starting', 'installing', 'running'].includes(e2bStatus) && !instantPreviewReady) {
    return {
      title: 'Booting isolated sandbox…',
      subtitle: 'Spinning up a secure environment for your app.',
      active: true,
    };
  }

  const appLabel = appType === 'ecommerce'
    ? 'e-commerce store'
    : appType === 'crm'
      ? 'CRM'
      : appType === 'saas'
        ? 'SaaS dashboard'
        : 'app';

  const stepMessages = {
    plan: {
      title: `Hang on — planning your ${appLabel}…`,
      subtitle: 'Understanding your prompt and choosing the right layout.',
    },
    codegen: {
      title: `Hang on — writing your ${appLabel} code…`,
      subtitle: 'Generating pages, components, and API routes.',
    },
    build: {
      title: 'Hang on — compiling your preview…',
      subtitle: 'Installing dependencies and preparing the live preview.',
    },
    preview: {
      title: 'Almost there…',
      subtitle: 'Putting the finishing touches on your preview.',
    },
  };

  if (buildStepId && stepMessages[buildStepId]) {
    return { ...stepMessages[buildStepId], active: true };
  }

  if (isGenerating || isBuilding) {
    return {
      title: vitePreviewLoading
        ? 'Hang on — starting live preview server…'
        : `Hang on — building your ${appLabel}…`,
      subtitle: vitePreviewLoading
        ? 'Installing dependencies and booting the Vite dev server.'
        : 'Preview appears here as soon as the first version is ready.',
      active: true,
    };
  }

  return {
    title: 'No preview yet',
    subtitle: 'Try Refresh or check the Activity tab for build logs.',
    active: false,
  };
}

function PreviewLoadingPanel({ copy, buildStepId }) {
  const activeStepIndex = BUILD_STEPS.indexOf(buildStepId);

  return (
    <div
      className="flex flex-1 items-center justify-center rounded-lg border border-dashed border-violet-500/20 bg-gradient-to-b from-violet-500/[0.06] to-transparent px-6 py-10 text-center"
      data-testid="app-builder-preview-loading-panel"
    >
      <div className="max-w-sm">
        <div className="relative mx-auto mb-5 flex h-16 w-16 items-center justify-center">
          {copy.active ? (
            <>
              <span className="absolute inset-0 animate-ping rounded-full bg-violet-500/15" />
              <span className="absolute inset-1 animate-pulse rounded-full bg-violet-500/10" />
              <Loader2 className="relative h-8 w-8 animate-spin text-violet-400" />
            </>
          ) : (
            <Sparkles className="h-8 w-8 text-violet-400/80" />
          )}
        </div>
        <p className="text-base font-semibold text-zinc-100" data-testid="app-builder-preview-loading-title">
          {copy.title}
        </p>
        <p className="mt-2 text-sm leading-relaxed text-zinc-400" data-testid="app-builder-preview-loading-description">
          {copy.subtitle}
        </p>
        {copy.active ? (
          <div className="mt-6 flex items-center justify-center gap-2" data-testid="app-builder-preview-step-dots">
            {BUILD_STEPS.map((step, index) => {
              const done = activeStepIndex > index;
              const current = activeStepIndex === index || (activeStepIndex === -1 && index === 0);
              return (
                <span
                  key={step}
                  className={`h-2 rounded-full transition-all ${
                    done
                      ? 'w-6 bg-emerald-400'
                      : current
                        ? 'w-6 animate-pulse bg-violet-400'
                        : 'w-2 bg-zinc-700'
                  }`}
                />
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function stampHtml(html, previewVersion, autoRefreshToken) {
  if (!html) return '';
  const stamp = `<!-- preview-v${previewVersion || 0}-${autoRefreshToken || '0'} -->`;
  if (html.includes('</head>')) {
    return html.replace('</head>', `${stamp}\n</head>`);
  }
  return `${stamp}\n${html}`;
}

function createPreviewBlobUrl(html) {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  return URL.createObjectURL(blob);
}

function isValidPreviewHtml(html) {
  const text = (html || '').trim();
  if (text.length < 200) return false;
  if (text.startsWith('{') && text.includes('"detail"')) return false;
  if (text.includes('Hang on — building your app')) return false;
  if (isAppShellHtml(text) || isViteReactScaffold(text) || isStaleReactPreviewHtml(text)) return false;
  if (text.includes('id="root"') && !text.includes('text/babel') && !text.includes('createRoot')) return false;
  return /<!DOCTYPE\s+html|<html[\s>]/i.test(text);
}

function isInstantPreviewCandidate(html) {
  const text = (html || '').trim();
  if (!isValidPreviewHtml(text)) return false;
  if (text.includes('text/babel') && isStaleReactPreviewHtml(text)) return false;
  return true;
}

function buildVitePreviewRequestUrl(baseUrl, subpath = '', token = '') {
  if (!baseUrl) return '';
  try {
    const url = new URL(baseUrl, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    const cleanPath = String(subpath || '').replace(/^\//, '');
    if (cleanPath) {
      url.pathname = `${url.pathname.replace(/\/$/, '')}/${cleanPath}`;
    }
    if (token) url.searchParams.set('token', token);
    return url.pathname + url.search;
  } catch {
    const root = String(baseUrl).split('?')[0].replace(/\/?$/, '/');
    const suffix = String(subpath || '').replace(/^\//, '');
    const path = suffix ? `${root}${suffix}` : root;
    return token ? `${path}?token=${encodeURIComponent(token)}` : path;
  }
}

async function vitePreviewResponseIsWaiting(response) {
  if (response.headers.get('x-preview-status') === 'waiting') return true;
  if (!response.ok) return false;
  const contentType = response.headers.get('content-type') || '';
  try {
    const text = await response.clone().text();
    if (contentType.includes('javascript')) {
      return text.includes('Vite preview is still starting');
    }
    if (contentType.includes('text/html')) {
      return text.includes('Hang on — building your app');
    }
  } catch {
    return false;
  }
  return false;
}

export default function PreviewPane({
  projectId,
  previewHtml,
  previewVersion,
  autoRefreshToken,
  files,
  projectName = 'App',
  vitePreviewUrl = null,
  projectState = 'idle',
  isGenerating = false,
  isLoadingProject = false,
  buildStepId = null,
  appType = null,
  onRefresh,
  onEnsurePreview,
}) {
  const tabBlobRef = useRef('');
  const viteFailStreakRef = useRef(0);
  const viteWasLiveRef = useRef(false);
  const viteEnsureCooldownRef = useRef(false);
  const [device, setDevice] = useState('desktop');
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [viteLive, setViteLive] = useState(false);
  const [e2bUrl, setE2bUrl] = useState('');
  const [e2bStatus, setE2bStatus] = useState('');

  const fileSignature = useMemo(
    () => Object.keys(files || {}).sort().join('|'),
    [files],
  );
  const hasFiles = Object.keys(files || {}).length > 0;

  const localHtml = useMemo(
    () => resolvePreviewDocument({
      files,
      preview_html: previewHtml,
      name: projectName,
    }),
    [files, previewHtml, projectName],
  );

  const instantReactHtml = useMemo(() => {
    const html = buildReactPreviewFromFiles(files, projectName);
    if (!html || html.length < 200 || isStaleReactPreviewHtml(html)) return '';
    return html;
  }, [files, projectName]);

  const serverPreviewSrc = useMemo(() => {
    if (!projectId) return '';
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') || '' : '';
    if (!token) return '';
    const apiRoot = API_URL || (typeof window !== 'undefined' ? window.location.origin : '');
    const qs = `token=${encodeURIComponent(token)}&v=${previewVersion || 0}&r=${refreshNonce}&f=${encodeURIComponent(fileSignature.slice(0, 80))}`;
    return `${apiRoot}/api/sitebrain/projects/${encodeURIComponent(projectId)}/preview?${qs}`;
  }, [projectId, previewVersion, refreshNonce, fileSignature]);

  const previewDocument = useMemo(() => {
    const candidates = [localHtml, previewHtml];
    for (const raw of candidates) {
      const html = (raw || '').trim();
      if (!html || isAppShellHtml(html) || isViteReactScaffold(html) || isStaleReactPreviewHtml(html)) continue;
      return stampHtml(html, previewVersion, autoRefreshToken);
    }
    return '';
  }, [localHtml, previewHtml, previewVersion, autoRefreshToken]);

  const [serverPreviewHtml, setServerPreviewHtml] = useState('');

  const activePreviewHtml = useMemo(() => {
    if (isInstantPreviewCandidate(previewHtml)) return previewHtml;
    if (isInstantPreviewCandidate(previewDocument)) return previewDocument;
    if (isInstantPreviewCandidate(instantReactHtml)) return instantReactHtml;
    if (isInstantPreviewCandidate(serverPreviewHtml)) return serverPreviewHtml;
    return '';
  }, [previewDocument, previewHtml, instantReactHtml, serverPreviewHtml]);

  const localInstantReady = useMemo(
    () => isInstantPreviewCandidate(previewHtml)
      || isInstantPreviewCandidate(previewDocument)
      || isInstantPreviewCandidate(instantReactHtml),
    [previewHtml, previewDocument, instantReactHtml],
  );

  useEffect(() => {
    if (!serverPreviewSrc) {
      return undefined;
    }

    let cancelled = false;
    const checkServerPreview = () => {
      fetch(serverPreviewSrc, { method: 'GET', credentials: 'same-origin' })
        .then(async (response) => {
          if (cancelled) return;
          if (!response.ok) {
            return;
          }
          if (response.headers.get('x-preview-status') === 'waiting') {
            return;
          }
          const contentType = response.headers.get('content-type') || '';
          if (!contentType.includes('text/html')) {
            return;
          }
          const text = await response.text();
          if (isValidPreviewHtml(text)) {
            setServerPreviewHtml(text);
          }
        })
        .catch(() => {});
    };

    checkServerPreview();
    if (!isGenerating && !['planning', 'generating', 'testing', 'deploying'].includes(projectState)) {
      return () => { cancelled = true; };
    }

    const timer = window.setInterval(checkServerPreview, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [serverPreviewSrc, localInstantReady, isGenerating, projectState]);

  // E2B isolated live preview: for React/Vite projects, boot a per-project
  // microVM dev server and use its public URL as the iframe src (a real
  // running artifact). No-op when E2B is unconfigured (start returns
  // "disabled") or the project isn't a buildable Vite app.
  const e2bApplicable = shouldUseSandpack(files) && !canBuildInstantReactPreview(files, projectName);
  useEffect(() => {
    if (!projectId || !e2bApplicable) {
      setE2bUrl('');
      setE2bStatus('');
      return undefined;
    }
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') || '' : '';
    if (!token) return undefined;
    const apiRoot = API_URL || (typeof window !== 'undefined' ? window.location.origin : '');
    const headers = { Authorization: `Bearer ${token}` };
    let cancelled = false;
    let timer = null;

    const poll = () => {
      fetch(`${apiRoot}/api/sitebrain/projects/${encodeURIComponent(projectId)}/e2b-preview/status?token=${encodeURIComponent(token)}`, { headers })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (cancelled || !data) return;
          setE2bStatus(data.status || '');
          if (data.status === 'ready' && data.url) {
            setE2bUrl(data.url);
          } else if (['disabled', 'skipped', 'error', 'dead', 'stopped', 'none'].includes(data.status)) {
            setE2bUrl('');
            if (['disabled', 'skipped', 'error'].includes(data.status)) {
              if (timer) clearInterval(timer);
              timer = null;
            }
          }
        })
        .catch(() => {});
    };

    fetch(`${apiRoot}/api/sitebrain/projects/${encodeURIComponent(projectId)}/e2b-preview/start`, { method: 'POST', headers })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        setE2bStatus(data.status || '');
        if (data.status === 'ready' && data.url) setE2bUrl(data.url);
        if (['disabled', 'skipped'].includes(data.status)) return;
        poll();
        timer = setInterval(poll, 3000);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [projectId, e2bApplicable, fileSignature, previewVersion]);

  const vitePreviewSrc = useMemo(() => {
    if (!vitePreviewUrl || !projectId) return null;
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') || '' : '';
    return buildVitePreviewRequestUrl(vitePreviewUrl, '', token);
  }, [vitePreviewUrl, projectId]);

  const catalogLayout = isCatalogHtml(previewDocument || previewHtml) && !instantReactHtml;
  const shouldPreferVitePreview = Boolean(vitePreviewUrl && projectId && shouldUseSandpack(files));

  const instantPreviewReady = !catalogLayout && (
    localInstantReady
    || isInstantPreviewCandidate(serverPreviewHtml)
    || (Boolean(projectId) && Boolean(serverPreviewSrc) && hasFiles)
  );

  const instantIframeSrc = (projectId && serverPreviewSrc && instantPreviewReady) ? serverPreviewSrc : '';

  useEffect(() => {
    if (!vitePreviewSrc || !shouldPreferVitePreview || instantPreviewReady) {
      if (!instantPreviewReady) {
        viteFailStreakRef.current = 0;
        setViteLive(false);
      }
      return undefined;
    }

    let cancelled = false;
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') || '' : '';
    const requestEnsure = () => {
      if (!onEnsurePreview || !projectId || viteEnsureCooldownRef.current) return;
      viteEnsureCooldownRef.current = true;
      onEnsurePreview(projectId);
      window.setTimeout(() => {
        viteEnsureCooldownRef.current = false;
      }, 8000);
    };
    const failThreshold = () => (viteWasLiveRef.current ? 5 : 2);
    const checkVite = async () => {
      const rootUrl = buildVitePreviewRequestUrl(vitePreviewUrl, '', token);
      try {
        const rootRes = await fetch(rootUrl, { method: 'GET', credentials: 'same-origin' });
        if (cancelled) return;
        const waiting = await vitePreviewResponseIsWaiting(rootRes);
        const live = rootRes.ok && !waiting;
        if (live) {
          viteFailStreakRef.current = 0;
          viteWasLiveRef.current = true;
          setViteLive(true);
          return;
        }
        viteFailStreakRef.current += 1;
        if (viteFailStreakRef.current >= failThreshold()) {
          viteWasLiveRef.current = false;
          setViteLive(false);
          requestEnsure();
        }
      } catch {
        if (cancelled) return;
        viteFailStreakRef.current += 1;
        if (viteFailStreakRef.current >= failThreshold()) {
          viteWasLiveRef.current = false;
          setViteLive(false);
          requestEnsure();
        }
      }
    };

    requestEnsure();
    checkVite();
    const timer = window.setInterval(checkVite, 1000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [vitePreviewSrc, vitePreviewUrl, shouldPreferVitePreview, instantPreviewReady, projectId, onEnsurePreview]);

  const buildingStates = ['planning', 'generating', 'testing', 'deploying', 'design_review', 'awaiting_requirements', 'awaiting_clarification'];
  const isBuilding = buildingStates.includes(projectState) || isGenerating;
  const awaitingUserInput = ['awaiting_clarification', 'awaiting_requirements'].includes(projectState);

  const sandpackFiles = useMemo(() => filesToSandpack(files, projectName), [files, projectName]);
  const sandpackReady = Object.keys(sandpackFiles || {}).length >= 2;
  const sandpackRenderable = useMemo(() => isSandpackRenderable(files, projectName), [files, projectName]);
  const preferSandpack = shouldUseSandpack(files) && sandpackReady;

  const useE2bPreview = Boolean(e2bUrl) && e2bStatus === 'ready';
  const e2bBooting = e2bApplicable && ['starting', 'installing', 'running'].includes(e2bStatus);
  const {
    useVitePreview,
    useSandpack,
    hasInstantPreview,
    vitePreviewLoading,
  } = resolvePreviewEngine({
    instantPreviewReady,
    preferSandpack,
    sandpackRenderable,
    useE2bPreview,
    shouldPreferVitePreview,
    viteLive,
  });

  const useVitePane = shouldPreferVitePreview && !useE2bPreview && !hasInstantPreview && !useSandpack;
  const showInstantPane = hasInstantPreview && (Boolean(instantIframeSrc) || Boolean(activePreviewHtml));

  const modeLabel = useE2bPreview
    ? 'E2B live preview'
      : useVitePreview
      ? 'Vite live preview'
      : useSandpack
        ? 'Sandpack live preview'
        : hasInstantPreview
          ? 'Instant preview'
          : vitePreviewLoading
            ? 'Starting live preview…'
            : e2bApplicable && ['starting', 'installing', 'running'].includes(e2bStatus)
              ? 'Booting isolated sandbox…'
              : catalogLayout
                ? 'Catalog preview'
                : isBuilding
                  ? 'Building…'
                  : 'Waiting for preview…';

  const deviceWidth = DEVICE_WIDTHS[device] || DEVICE_WIDTHS.desktop;

  const handleRefresh = useCallback(() => {
    setServerPreviewHtml('');
    setRefreshNonce((n) => n + 1);
    onRefresh?.();
  }, [onRefresh]);

  const openInNewTab = useCallback(() => {
    if (useE2bPreview && e2bUrl) {
      const opened = window.open(e2bUrl, '_blank', 'noopener,noreferrer');
      if (!opened) toast.message('Allow popups for this site');
      return;
    }
    if ((useVitePreview || useVitePane) && vitePreviewSrc) {
      const opened = window.open(vitePreviewSrc, '_blank', 'noopener,noreferrer');
      if (!opened) toast.message('Allow popups for this site');
      return;
    }
    if (serverPreviewSrc) {
      const opened = window.open(serverPreviewSrc, '_blank', 'noopener,noreferrer');
      if (!opened) toast.message('Allow popups for this site');
      return;
    }
    const htmlForTab = activePreviewHtml;
    if (!htmlForTab) {
      toast.error('No preview available yet');
      return;
    }
    if (tabBlobRef.current) {
      URL.revokeObjectURL(tabBlobRef.current);
    }
    const url = createPreviewBlobUrl(htmlForTab);
    tabBlobRef.current = url;
    const opened = window.open(url, '_blank', 'noopener,noreferrer');
    if (!opened) toast.message('Allow popups for this site');
    window.setTimeout(() => {
      if (tabBlobRef.current === url) {
        URL.revokeObjectURL(url);
        tabBlobRef.current = '';
      }
    }, 120_000);
  }, [activePreviewHtml, useVitePreview, useVitePane, vitePreviewSrc, useE2bPreview, e2bUrl, serverPreviewSrc]);

  useEffect(() => () => {
    if (tabBlobRef.current) URL.revokeObjectURL(tabBlobRef.current);
  }, []);

  const showContent = !awaitingUserInput && (useE2bPreview || useVitePreview || useVitePane || useSandpack || showInstantPane);
  const waitingForE2bOnly = e2bBooting && !showContent;
  const loadingCopy = getPreviewLoadingCopy({
    isLoadingProject,
    projectId,
    isGenerating,
    isBuilding: isBuilding || vitePreviewLoading || waitingForE2bOnly,
    buildStepId,
    appType,
    e2bApplicable,
    e2bStatus,
    vitePreviewLoading,
    instantPreviewReady,
    projectState,
    hasFiles,
  });

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-white/[0.06] bg-white/[0.02]" data-testid="app-builder-preview-pane">
      <div className="flex shrink-0 items-center gap-2 border-b border-white/[0.06] bg-white/[0.03] px-3 py-2">
        <div className="flex items-center gap-1 rounded-lg border border-white/[0.08] bg-[#0d0d0d] p-0.5">
          {[
            { id: 'desktop', icon: Monitor, label: 'Desktop' },
            { id: 'tablet', icon: Tablet, label: 'Tablet' },
            { id: 'mobile', icon: Smartphone, label: 'Mobile' },
          ].map(({ id, icon: Icon, label }) => (
            <button
              key={id}
              type="button"
              title={label}
              onClick={() => setDevice(id)}
              className={`inline-flex h-8 w-8 items-center justify-center rounded-md transition ${
                device === id ? 'bg-zinc-700 text-white' : 'text-zinc-500 hover:text-zinc-300'
              }`}
              data-testid={`app-builder-preview-device-${id}-button`}
            >
              <Icon className="h-4 w-4" />
            </button>
          ))}
        </div>

        <div className="min-w-0 flex-1 truncate rounded-md border border-white/[0.06] bg-[#0d0d0d] px-3 py-1.5 text-xs text-zinc-400" data-testid="app-builder-preview-mode-label">
          {modeLabel}
        </div>

        <button
          type="button"
          onClick={handleRefresh}
          disabled={!projectId}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-zinc-700 text-zinc-400 hover:bg-zinc-800 hover:text-white disabled:opacity-40"
          title="Refresh preview"
          data-testid="app-builder-refresh-preview-inline"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={openInNewTab}
          disabled={!showInstantPane && !useVitePreview && !useVitePane}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-zinc-700 text-zinc-400 hover:bg-zinc-800 hover:text-white disabled:opacity-40"
          title="Open in new tab"
          data-testid="app-builder-open-preview-new-tab-button"
        >
          <ExternalLink className="h-4 w-4" />
        </button>
        <div className="hidden items-center gap-1.5 rounded-md border border-emerald-500/20 bg-emerald-500/10 px-2 py-1 text-[10px] uppercase tracking-wider text-emerald-300 sm:inline-flex" data-testid="app-builder-preview-version-badge">
          <Sparkles className="h-3 w-3" /> v{previewVersion || 0}
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col bg-[#080808] p-3">
        <div className="mb-2 flex items-center gap-2 px-1">
          <Eye className="h-4 w-4 text-zinc-500" />
          <span className="text-xs font-medium text-zinc-400" data-testid="app-builder-preview-state-label">
            {showContent ? modeLabel : loadingCopy.title}
          </span>
        </div>

        {!showContent ? (
          <PreviewLoadingPanel copy={loadingCopy} buildStepId={buildStepId} />
        ) : useE2bPreview ? (
          <div
            className="relative flex min-h-0 flex-1 items-stretch justify-center overflow-hidden rounded-lg border border-white/[0.08] bg-zinc-100"
            data-testid="app-builder-e2b-preview"
          >
            <div
              className="h-full min-h-[320px] transition-all duration-200"
              style={{ width: deviceWidth, maxWidth: '100%' }}
            >
              <iframe
                key={`e2b-${projectId}-${previewVersion}-${refreshNonce}`}
                title="Snagcart App Preview"
                src={e2bUrl}
                className="h-full w-full border-0 bg-white"
                data-testid="app-builder-preview-iframe"
              />
            </div>
          </div>
        ) : useVitePreview || useVitePane ? (
          <div
            className="relative flex min-h-0 flex-1 items-stretch justify-center overflow-hidden rounded-lg border border-white/[0.08] bg-zinc-100"
            data-testid="app-builder-vite-preview"
          >
            <div
              className="h-full min-h-[320px] transition-all duration-200"
              style={{ width: deviceWidth, maxWidth: '100%' }}
            >
              <iframe
                key={`vite-${projectId}-${previewVersion}-${refreshNonce}`}
                title="Snagcart Vite Preview"
                src={vitePreviewSrc}
                className="h-full w-full border-0 bg-white"
                data-testid="app-builder-preview-iframe"
                referrerPolicy="same-origin"
              />
            </div>
          </div>
        ) : showInstantPane ? (
          <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-white/[0.08] bg-zinc-100">
            <div className="flex min-h-0 flex-1 items-stretch justify-center">
              <div
                className="h-full min-h-[320px] w-full transition-all duration-200"
                style={{ width: deviceWidth, maxWidth: '100%' }}
              >
                {instantIframeSrc ? (
                  <iframe
                    key={`instant-server-${projectId}-${previewVersion}`}
                    title="Snagcart App Preview"
                    src={instantIframeSrc}
                    className="h-full w-full border-0 bg-white"
                    data-testid="app-builder-preview-iframe"
                  />
                ) : (
                  <iframe
                    key={`instant-${projectId || 'new'}`}
                    title="Snagcart App Preview"
                    srcDoc={activePreviewHtml}
                    sandbox={PREVIEW_SANDBOX}
                    className="h-full w-full border-0 bg-white"
                    data-testid="app-builder-preview-iframe"
                  />
                )}
              </div>
            </div>
          </div>
        ) : useSandpack ? (
          <div
            className="relative flex min-h-0 flex-1 items-stretch justify-center overflow-hidden rounded-lg border border-white/[0.08] bg-zinc-900"
            data-testid="app-builder-sandpack-preview"
          >
            <div
              className="h-full min-h-[320px] w-full transition-all duration-200"
              style={{ maxWidth: device === 'desktop' ? '100%' : deviceWidth }}
            >
              <SandpackPreview
                files={files}
                projectName={projectName}
                previewVersion={previewVersion}
              />
            </div>
          </div>
        ) : (
          <PreviewLoadingPanel copy={loadingCopy} buildStepId={buildStepId} />
        )}
      </div>
    </div>
  );
}
